/* eslint-disable @atlassian/tangerine/import/no-parent-imports */

import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { createPortal } from 'react-dom';
import { useQueryLoader } from 'react-relay';

import { cssMap } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box } from '@atlaskit/primitives/compiled';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import UFOSegment from '@atlaskit/react-ufo/segment';
import { type BackendExperiment } from '@atlassian/search-experiment';

import { initialState as bootstrapInitialState } from '../../common/constants/bootstrap';
import type { AllFilterStates } from '../../common/constants/filters/types';
import { PrimaryProductKeys, type ProductKeys } from '../../common/constants/products';
import type { BootstrapVariables, SearchVariables } from '../../common/constants/relay/types';
import { ResultsStates } from '../../common/constants/results';
import { useAppContext } from '../../common/ui/app-context';
import { ErrorComponent } from '../../common/ui/error';
import type { Sections } from '../../common/ui/filters/app-filter-sidebar/app-filters/dynamic-sections';
import { ResetProductsWithTimeout } from '../../common/ui/filters/app-filter-sidebar/reset-products-with-timeout';
import { Landing } from '../../common/ui/landing';
import { Layout } from '../../common/ui/layout';
import { Body } from '../../common/ui/layout-body';
import { Header } from '../../common/ui/layout-header';
import { ResultsLoading } from '../../common/ui/results-loading';
import { searchResultsLoadUFOExperienceError } from '../../common/ui/ufo/experiences';
import { getApplicationMode, getUser } from '../../common/utils/bootstrap';
import { OAuthSource } from '../../common/utils/events/oauth-events';
import { onTrackAvailableProducts } from '../../common/utils/events/track-available-products';
import {
	serve3PBulkConnectWithoutExposure,
	serveSearchPageInline3PBannerRevampedExperiment,
	serveSearchPageTimeoutsUX,
} from '../../common/utils/experiments';
import {
	PlacementPortalContainerProvider,
	setSearchPagePlacementClaimed,
	TrackImpressionProvider,
} from '../../common/utils/post-office-placement/placement-portal-context';
import { SearchPageResultsInlineBannerPlacement } from '../../common/utils/post-office-placement/search-page-results-inline-banner-placement';
import { getBootstrapVariables, getSearchVariables } from '../../common/utils/relay/get-variables';
import { disposeQueryUpdatedTime } from '../../common/utils/relay/query-updated-time';
import { useBannerEligibleProductsOrder } from '../../controllers/3p-smart-link-usage';
import { useAnalytics } from '../../controllers/analytics';
import { ErrorBoundary } from '../../controllers/error-boundary';
import { useLogException } from '../../controllers/error-boundary/utils';
import { getSelectedFilters as getFilters } from '../../controllers/filters/selected';
import { useOAuthHandlers } from '../../controllers/oauth';
import {
	useAvailableProducts,
	useBootstrapState,
	useClearReferrer,
	useFeatures,
	useQueryParams,
	useThirdPartyConfigs,
} from '../../controllers/store/bootstrap';
import {
	initialState as searchInitialState,
	useSearchActions,
} from '../../controllers/store/search';
import { BulkConnectModal } from '../search-page/bulk-connect-modal';
import Filters from '../search-page/filters';
import Input from '../search-page/input';
import { Error } from '../search-page/results/error';
import SmartAnswers from '../search-page/smart-answers';

import { AssetsAppMessageBanner } from './message-banners/assets-app-message-banner';
import { NewProductMessageBanner } from './message-banners/new-product-message-banner';
import { SearchPageQueryOrchestrator } from './query-plan';
import { RightPanel, RightPanelContainer, RightPanelSkeleton } from './right-panel';
import RightPanelQuery, {
	type rightPanelQuery,
} from './right-panel/__generated__/rightPanelQuery.graphql';
import SearchResultsCqlQuery, {
	type searchResultsCqlQuery,
} from './search-results/__generated__/searchResultsCqlQuery.graphql';
import SearchResultsQuery, {
	type searchResultsQuery,
} from './search-results/__generated__/searchResultsQuery.graphql';
import { SearchResults } from './search-results/search-results';
import { SearchResultsCQL } from './search-results/search-results-cql';
import { getSearchConfig } from './utils/get-search-config';
import { getSearchPageMode, type SearchPageMode } from './utils/get-search-page-mode';

type VoidFunc = () => void;

export type SearchPageRelayProps = {
	aggAbsoluteUrl?: string;
	headerOffset?: number;
	debugPortalRef?: React.RefObject<HTMLDivElement>;
};

const autoCorrectStyles = cssMap({
	root: {
		'&:empty': {
			display: 'none',
		},
	},
});

/**
 * Memoized to prevent re-renders when SearchPageLayout re-renders due to bootstrap
 * context/store changes. SearchPageBody receives all reactive data from internal hooks
 * (useAppContext, useSearchActions, etc.) and local useState mirrors (set by
 * SearchPageQueryOrchestrator), not from parent props. Props are stable: aggAbsoluteUrl
 * is a string, debugPortalRef is a useRef. This keeps SearchPageBody off the critical
 * path for query start.
 */
const SearchPageBody = React.memo(({ aggAbsoluteUrl, debugPortalRef }: SearchPageRelayProps) => {
	const [relaySearchResults, loadSearchResultsQuery, disposeSearchResultsQuery] =
		useQueryLoader<searchResultsQuery>(SearchResultsQuery);
	const [relayRightPanel, loadRightPanelQuery, disposeRightPanelQuery] =
		useQueryLoader<rightPanelQuery>(RightPanelQuery);
	const [searchResultsCQLQueryReference, loadSearchResultsCQLQuery, disposeSearchResultsCQLQuery] =
		useQueryLoader<searchResultsCqlQuery>(SearchResultsCqlQuery);
	const [resultsState, setResultsState] = useState<ResultsStates>(ResultsStates.INITIAL);
	const { queryParams, isAdminHubAIEnabled, isRovoLLMEnabled } = useAppContext();
	const { clearAllFilters, resetSort } = useSearchActions();
	const clearReferrer = useClearReferrer();
	const logError = useLogException();
	const { fireAnalyticsEvent } = useAnalytics();
	const [features] = useFeatures();
	const isSideBySideEnabled = features?.sideBySide?.enabled ?? false;

	// Local state for bootstrap, search, and selected input filters to update when Relay query resolves
	const [bootstrap, setBootstrap] = useState<BootstrapVariables>(() =>
		getBootstrapVariables(bootstrapInitialState),
	);
	const [search, setSearch] = useState<SearchVariables>(() =>
		getSearchVariables(searchInitialState),
	);
	const [federatedSlackCount, setFederatedSlackCount] = useState<number | null>(null);
	const [federatedOutlookMailCount, setFederatedOutlookMailCount] = useState<number | null>(null);
	const [federatedGmailCount, setFederatedGmailCount] = useState<number | null>(null);
	const [hasRecordedAvailableProducts, setHasRecordedAvailableProducts] = useState(false);

	const [productsWithTimeout, setProductsWithTimeout] = useState<ProductKeys[]>();
	const [personalisedProductSections, setPersonalisedProductSections] =
		useState<Map<Sections, ProductKeys[]>>();

	// Sync thirdPartyConfigs from the bootstrap store into the local snapshot
	// so the right-panel sidebar reflects OAuth changes immediately.
	const [liveThirdPartyConfigs] = useThirdPartyConfigs();
	useEffect(() => {
		setBootstrap((prev) => {
			if (prev.thirdPartyConfigs === liveThirdPartyConfigs) {
				return prev;
			}
			return { ...prev, thirdPartyConfigs: liveThirdPartyConfigs };
		});
	}, [liveThirdPartyConfigs]);

	// Error boundary reset handling - creates a ref to store the reset callback
	// Calling resetErrorBoundary() will invoke the reset on the ErrorBoundary component without a remount
	const errorBoundaryResetCbRef = useRef<VoidFunc | undefined>(undefined);
	const setErrorBoundaryResetCb = useCallback((cb?: VoidFunc) => {
		errorBoundaryResetCbRef.current = cb; // undefined acts as a cleanup
	}, []);
	const resetErrorBoundary = useCallback(() => {
		errorBoundaryResetCbRef.current?.();
	}, []);

	const bodyRef = useRef<HTMLDivElement>(null);
	const mainContentRef = useRef<HTMLDivElement>(null);
	const [maxResultsToShow, setMaxResultsToShow] = useState<number | undefined>(undefined);
	const [mainContentWidth, setMainContentWidth] = useState<number | undefined>(undefined);

	const {
		cloudId,
		primaryProduct,
		searchSessionId,
		isRovoEnabled,
		allFilterExcludedProducts,
		thirdPartyConfigs,
		experimentConfig,
		productEdition,
		productDataRegions,
		isJiraIssueIndexed,
		user: maybeUser,
		nouns,
		isNounsAvailable,
		referrer,
	} = bootstrap;

	/**
	 * Dealing with the usage of local mirror of search state will come in a later PR for M3.
	 * For the time being, having the local mirror continue to be synced with search store
	 * is fine. The only difference will be that we fire search requests earlier than before,
	 * but rendering stays the same. At the end of M3, the goal will be to no longer need
	 * the local mirror. The main risk is not seeing the loading state immediately and potentially
	 * the right panel loading state being incorrect on first render (unlikely).
	 */
	const {
		cql: cql_SEARCH_LOCAL_MIRROR,
		searchQuery: searchQuery_SEARCH_LOCAL_MIRROR,
		filters: filters_SEARCH_LOCAL_MIRROR,
		jsmIncidentFilterIds,
		selectedProducts: selectedProducts_SEARCH_LOCAL_MIRROR,
		availableProducts: availableProducts_SEARCH_LOCAL_MIRROR,
		selectedDatasource: selectedDatasource_SEARCH_LOCAL_MIRROR,
	} = search;

	// Bootstrap params outside of local mirror state
	const [thirdPartyConfigs_NOT_LOCAL_MIRROR] = useThirdPartyConfigs();
	const availableProducts_NOT_LOCAL_MIRROR = useAvailableProducts();

	// Search parameters independent of search store only defined when FG is on
	const {
		cql: cql_NO_RSS,
		searchQuery: searchQuery_NO_RSS,
		originalQuery: originalQuery_NO_RSS,
		filters: filters_NO_RSS_DO_NOT_USE_FOR_RENDERING,
		selectedProducts: selectedProducts_NO_RSS,
		selectedDatasource: selectedDatasource_NO_RSS,
	} = useMemo(() => {
		if (!fg('rovo_search_page_search_query_without_rss')) {
			return {
				cql: undefined,
				searchQuery: undefined,
				originalQuery: undefined,
				filters: undefined,
				selectedProducts: undefined,
				selectedDatasource: undefined,
			};
		}

		return getSearchConfig({
			cloudId: cloudId || '',
			queryParams,
			thirdPartyConfigs: thirdPartyConfigs_NOT_LOCAL_MIRROR,
		});
	}, [cloudId, queryParams, thirdPartyConfigs_NOT_LOCAL_MIRROR]);

	// Prefer NO_RSS variables over local mirror state when FG is on
	const cql = useMemo(
		() => (fg('rovo_search_page_search_query_without_rss') ? cql_NO_RSS : cql_SEARCH_LOCAL_MIRROR),
		[cql_NO_RSS, cql_SEARCH_LOCAL_MIRROR],
	);
	const searchQuery = useMemo(
		() =>
			fg('rovo_search_page_search_query_without_rss')
				? (searchQuery_NO_RSS ?? '')
				: searchQuery_SEARCH_LOCAL_MIRROR,
		[searchQuery_NO_RSS, searchQuery_SEARCH_LOCAL_MIRROR],
	);
	const filters = useMemo(
		() =>
			fg('rovo_search_page_search_query_without_rss')
				? (filters_NO_RSS_DO_NOT_USE_FOR_RENDERING as Record<string, AllFilterStates | undefined>) // filters will exist when the FG is on
				: filters_SEARCH_LOCAL_MIRROR,
		[filters_NO_RSS_DO_NOT_USE_FOR_RENDERING, filters_SEARCH_LOCAL_MIRROR],
	);
	const selectedProducts = useMemo(
		() =>
			fg('rovo_search_page_search_query_without_rss')
				? (selectedProducts_NO_RSS as string[]) // selectedProducts will exist when the FG is on
				: selectedProducts_SEARCH_LOCAL_MIRROR,
		[selectedProducts_NO_RSS, selectedProducts_SEARCH_LOCAL_MIRROR],
	);
	const selectedDatasource = useMemo(
		() =>
			fg('rovo_search_page_search_query_without_rss')
				? (selectedDatasource_NO_RSS ?? undefined)
				: (selectedDatasource_SEARCH_LOCAL_MIRROR ?? undefined),
		[selectedDatasource_NO_RSS, selectedDatasource_SEARCH_LOCAL_MIRROR],
	);
	const availableProducts = useMemo(
		() =>
			fg('rovo_search_page_search_query_without_rss')
				? availableProducts_NOT_LOCAL_MIRROR
				: availableProducts_SEARCH_LOCAL_MIRROR,
		[availableProducts_NOT_LOCAL_MIRROR, availableProducts_SEARCH_LOCAL_MIRROR],
	);

	const selectedProduct = selectedProducts[0];

	const appMode = getApplicationMode({ isRovoEnabled });

	const user = useMemo(() => getUser(maybeUser), [maybeUser]);
	/**
	 * QS-7760
	 *
	 * Our search page DOM looks like this:
	 * - 1. Input
	 * - 2. Autocorrect
	 * - 3. SAIN
	 * - 4. Results list
	 *
	 * 2 and 4 comes from the search query, which suspends.
	 * 3 needs to be rendered immediately to begin the SAIN query (which can show before results return), but also needs to render between 2 and 4 in DOM
	 * So we use a portal to insert 2 above 3
	 * Otherwise 3 will be within the common suspense parent of 2 and 4, and not render until the query is resolved
	 * We also dont want to shw 3 in the suspense fallback, as when suspense finishes a remount is triggered (which flashes the SAIN UI)
	 *
	 * We considered splitting 2 and 4 but would need to implement `usePreloadedQuery` independently (with all pagination, query vars etc) but decided to take this approach to reduce the risk surface/complexity of the queries changing
	 */
	const autoCorrectPortalRef = useRef<HTMLDivElement>(null);
	const autoCorrectPortal = <Box xcss={autoCorrectStyles.root} ref={autoCorrectPortalRef} />;

	// Ref to the SAIN portal which is used by the Navigational Card that's rendered in the smart answer component
	const smartAnswersPortalRef = useRef<HTMLDivElement>(null);

	// Ref to a full-width portal target in Body, used to render the Jira NL SAIN card above the two-column layout
	const fullWidthSainPortalRef = useRef<HTMLDivElement>(null);

	// Ref for backend experiment data used when fetching search results
	const backendExperimentRef = useRef<BackendExperiment>();

	// Ideally we can refactor filters so that we run this function only when the filters change.
	// This would allow us to have the object in state.
	const getSelectedFilters = useMemo(
		() => () =>
			getFilters({
				filters,
				selectedProducts,
				availableProducts,
				allFilterExcludedProducts,
				thirdPartyConfigs,
				jsmIncidentFilterIds,
				currentUserId: user?.id,
			}),
		[
			filters,
			selectedProducts,
			availableProducts,
			allFilterExcludedProducts,
			thirdPartyConfigs,
			jsmIncidentFilterIds,
			user?.id,
		],
	);

	const resultsLoading = (
		<UFOLoadHold name="search-page-results-loading">
			<ResultsLoading rowCount={10} />
		</UFOLoadHold>
	);

	const resultsConfigLoading = (
		<UFOLoadHold name="search-page-results-config-loading">
			<ResultsLoading rowCount={10} />
		</UFOLoadHold>
	);

	/**
	 * Our use of:
	 * 1. Suspense -> RightpanelContainer
	 * 2. rightPanelLoading -> Suspense<fallback={rightPanelLoading}>
	 * 3. AppFilterSidebarLoading
	 *
	 * Is so that we get a loading state on counts:
	 * - 3 is the skeleton state (during bootstrap, no connectors) - HOLD(seach-page-right-panel-config-loading)
	 * - 2 is the suspense state (during search, no counts, has connectors) - HOLD(seach-page-right-panel-counts-loading)
	 * - 1 is the end state (counts!)
	 *
	 * Note that FG(rovo-search-right-rail-personalisation) modifies this to:
	 * - 3 is the skeleton state (during bootstrap, no connectors) - HOLD(seach-page-right-panel-config-loading)
	 * - 2 is the suspense state (during counts query, no RR rendered) - HOLD(seach-page-right-panel-loading)
	 * - 1 is the end state (connectors + counts!)
	 *
	 * Problem is when we render 1, we throw away 2 which isn't great for performance.
	 *
	 * https://product-fabric.atlassian.net/browse/QS-7802 for improvement
	 */
	const rightPanelWithoutResultCounts = (
		<RightPanel
			cloudId={cloudId}
			searchQuery={searchQuery}
			primaryProduct={primaryProduct}
			experimentConfig={experimentConfig}
			selectedProducts={selectedProducts}
			user={user}
			appMode={appMode}
			selectedProduct={selectedProduct}
			selectedDatasource={selectedDatasource}
			availableNouns={nouns}
			availableProducts={availableProducts}
			isNounsAvailable={isNounsAvailable}
			thirdPartyConfigs={thirdPartyConfigs}
			personalisedProductSections={personalisedProductSections}
			productsWithTimeout={productsWithTimeout}
		/>
	);

	const rightPanelWithLoadingResultCounts = (
		<RightPanel
			cloudId={cloudId}
			searchQuery={searchQuery}
			primaryProduct={primaryProduct}
			experimentConfig={experimentConfig}
			selectedProducts={selectedProducts}
			user={user}
			appMode={appMode}
			selectedProduct={selectedProduct}
			selectedDatasource={selectedDatasource}
			availableNouns={nouns}
			availableProducts={availableProducts}
			isNounsAvailable={isNounsAvailable}
			thirdPartyConfigs={thirdPartyConfigs}
			personalisedProductSections={personalisedProductSections}
			productsWithTimeout={productsWithTimeout}
			resultCountsLoading={true}
		/>
	);

	// In this state the RR has connectors but no counts
	const rightPanelWithoutResultCountsLoading = (
		<UFOLoadHold name="search-page-right-panel-counts-loading">
			<ResetProductsWithTimeout setProductsWithTimeout={setProductsWithTimeout} />
			{rightPanelWithoutResultCounts}
		</UFOLoadHold>
	);

	// In this state, we're waiting for 1P+3P config to be available
	const rightPanelConfigLoadingSkeleton = (
		<UFOLoadHold name="search-page-right-panel-config-loading">
			<ResetProductsWithTimeout setProductsWithTimeout={setProductsWithTimeout} />
			<RightPanelSkeleton />
		</UFOLoadHold>
	);

	// In this state, we're waiting for the RR to be ready
	const rightPanelLoadingSkeleton = (
		<UFOLoadHold name="search-page-right-panel-loading">
			<ResetProductsWithTimeout setProductsWithTimeout={setProductsWithTimeout} />
			<RightPanelSkeleton />
		</UFOLoadHold>
	);

	// In this state, we're waiting for the RR to be ready, but apps are visible
	const rightPanelLoadingSkeletonWithAppsVisible = (
		<UFOLoadHold name="search-page-right-panel-counts-loading">
			<ResetProductsWithTimeout setProductsWithTimeout={setProductsWithTimeout} />
			{rightPanelWithLoadingResultCounts}
		</UFOLoadHold>
	);

	// Log a health metric for accessible products
	useEffect(() => {
		if (fg('rovo-search-accessible-products-healthmetric')) {
			// When null or empty bootstrap isn't ready. Even failed bootstrap will have primaryProduct in the set
			if (availableProducts && availableProducts.length > 0 && !hasRecordedAvailableProducts) {
				fireAnalyticsEvent(
					onTrackAvailableProducts({
						availableProducts,
						searchSessionId,
					}),
				);
				setHasRecordedAvailableProducts(true);
			}
		}
	}, [availableProducts, hasRecordedAvailableProducts, fireAnalyticsEvent, searchSessionId]);

	useEffect(() => {
		if (!serveSearchPageTimeoutsUX()) {
			return;
		}

		if (searchQuery.length === 0) {
			setProductsWithTimeout(undefined);
		}
	}, [searchQuery]);

	// Clean up on unmount
	useEffect(
		() => () => {
			// Dispose query updated time
			disposeQueryUpdatedTime();
		},
		[],
	);

	const bootstrapReady = availableProducts.length > 0;

	// Store the trackImpression callback provided by the PostOffice placement's onMessageViewed.
	// This is called later by OAuthContainer when the banner becomes visible in search results.
	const trackImpressionRef = useRef<(() => void) | null>(null);
	const onBannerVisible = useCallback(() => {
		trackImpressionRef.current?.();
	}, []);
	const onBannerMessageViewed = useCallback((trackImpression: () => void) => {
		trackImpressionRef.current = trackImpression;
	}, []);

	// Create a detached DOM node for the search-page-results-inline placement portal.
	// The placement component is mounted early (via createPortal) so its fetch starts in parallel with search, since requests are only made on placement mount.
	// When results render, <PlacementSlot> moves this node into the relevant position in the results list.
	const bannerPortalContainerRef = useRef<HTMLDivElement | null>(null);
	if (typeof document !== 'undefined' && !bannerPortalContainerRef.current) {
		bannerPortalContainerRef.current = document.createElement('div');
		bannerPortalContainerRef.current.style.display = 'none';
	}

	const hasAnyFilterSelected = useMemo(() => {
		return fg('rovo_search_page_search_query_without_rss')
			? Boolean(selectedProduct) || getSelectedFilters().hasFiltersSelected
			: Boolean(selectedProduct) || Object.values(filters).some((filter) => filter?.hasSelection);
	}, [selectedProduct, filters, getSelectedFilters]);

	// Derive mode from the same local mirror state used for rendering.
	const mode: SearchPageMode = useMemo(
		() =>
			getSearchPageMode({
				bootstrapReady,
				availableProducts,
				selectedProduct,
				searchQuery,
				hasAnyFilterSelected,
				cql,
			}),
		[bootstrapReady, availableProducts, selectedProduct, searchQuery, hasAnyFilterSelected, cql],
	);

	const newProductMessageBanner = useMemo(
		() => (
			<NewProductMessageBanner
				searchQuery={searchQuery}
				selectedProduct={selectedProduct}
				selectedProducts={selectedProducts}
				primaryProduct={primaryProduct}
				cloudId={cloudId}
				experimentConfig={experimentConfig}
				user={user}
				applicationMode={appMode}
			/>
		),
		[
			searchQuery,
			selectedProduct,
			selectedProducts,
			primaryProduct,
			cloudId,
			experimentConfig,
			user,
			appMode,
		],
	);

	const isSearchPagePOBannerPlacementEnabled = serveSearchPageInline3PBannerRevampedExperiment();

	// Claim placement ownership so Quick Find's fetch-results does not render a duplicate banner.
	useEffect(() => {
		if (isSearchPagePOBannerPlacementEnabled && bannerPortalContainerRef.current) {
			setSearchPagePlacementClaimed(true);
		}
		return () => {
			setSearchPagePlacementClaimed(false);
		};
	}, [isSearchPagePOBannerPlacementEnabled]);

	const bannerEligibleProductsOrder = useBannerEligibleProductsOrder({
		isOnSearchDialog: false,
		thirdPartyConfigs,
		availableProducts,
	});

	const bootstrapForPlacementReady =
		bannerEligibleProductsOrder.length > 0 && Object.keys(thirdPartyConfigs).length > 0;

	const content = (
		<>
			{/* Mount placement early into detached container so its fetch starts in parallel with search */}
			{isSearchPagePOBannerPlacementEnabled &&
				bootstrapForPlacementReady &&
				bannerPortalContainerRef.current &&
				createPortal(
					<SearchPageResultsInlineBannerPlacement
						thirdPartyConfigs={thirdPartyConfigs}
						availableProductsOrder={bannerEligibleProductsOrder}
						useOAuthHandlers={useOAuthHandlers}
						onMessageViewed={onBannerMessageViewed}
					/>,
					bannerPortalContainerRef.current,
				)}
			<SearchPageQueryOrchestrator
				loadSearchResultsQuery={loadSearchResultsQuery}
				disposeSearchResultsQuery={disposeSearchResultsQuery}
				loadRightPanelQuery={loadRightPanelQuery}
				disposeRightPanelQuery={disposeRightPanelQuery}
				loadSearchResultsCQLQuery={loadSearchResultsCQLQuery}
				disposeSearchResultsCQLQuery={disposeSearchResultsCQLQuery}
				setBootstrap={setBootstrap}
				setSearch={setSearch}
				setResultsState={setResultsState}
				resetErrorBoundary={resetErrorBoundary}
				bodyRef={bodyRef}
				setMaxResultsToShow={setMaxResultsToShow}
				backendExperimentRef={backendExperimentRef}
				isAdminHubAIEnabled={isAdminHubAIEnabled}
				isRovoLLMEnabled={isRovoLLMEnabled}
				logError={logError}
				cql={cql_NO_RSS}
				searchQuery={searchQuery_NO_RSS}
				originalQuery={originalQuery_NO_RSS}
				filters={filters_NO_RSS_DO_NOT_USE_FOR_RENDERING}
				selectedProducts={selectedProducts_NO_RSS}
				selectedDatasource={selectedDatasource_NO_RSS}
			/>
			<Body
				bodyRef={bodyRef}
				mainContentRef={mainContentRef}
				setMainContentWidth={setMainContentWidth}
				selectedProduct={selectedProduct}
				primaryProduct={primaryProduct}
				sideBySide={isSideBySideEnabled}
				fullWidthSainPortalRef={fullWidthSainPortalRef}
				rightPanel={
					<ErrorBoundary
						section="SearchPageRelayRightPanel"
						fallback={rightPanelWithoutResultCounts}
					>
						{bootstrapReady ? (
							// In RESULTS mode we suspend on the counts query, otherwise we render the prequery right rail.
							mode === 'RESULTS' ? (
								<Suspense
									fallback={
										fg('rovo-search-right-rail-personalisation')
											? personalisedProductSections && serveSearchPageTimeoutsUX()
												? rightPanelLoadingSkeletonWithAppsVisible
												: rightPanelLoadingSkeleton
											: rightPanelWithoutResultCountsLoading
									}
								>
									{relayRightPanel ? (
										<UFOSegment name="search-page-right-panel">
											<RightPanelContainer
												cloudId={cloudId}
												searchQuery={searchQuery}
												primaryProduct={primaryProduct}
												experimentConfig={experimentConfig}
												selectedProducts={selectedProducts}
												user={user}
												appMode={appMode}
												rightPanelQuery={relayRightPanel}
												selectedProduct={selectedProduct}
												selectedDatasource={selectedDatasource}
												availableNouns={nouns}
												availableProducts={availableProducts}
												isNounsAvailable={isNounsAvailable}
												thirdPartyConfigs={thirdPartyConfigs}
												federatedSlackCount={federatedSlackCount}
												federatedOutlookMailCount={federatedOutlookMailCount}
												federatedGmailCount={federatedGmailCount}
												personalisedProductSections={personalisedProductSections}
												setPersonalisedProductSections={setPersonalisedProductSections}
												productsWithTimeout={productsWithTimeout}
											/>
										</UFOSegment>
									) : (
										rightPanelWithoutResultCountsLoading
									)}
								</Suspense>
							) : (
								<>
									<ResetProductsWithTimeout setProductsWithTimeout={setProductsWithTimeout} />
									{rightPanelWithoutResultCounts}
								</>
							)
						) : (
							rightPanelConfigLoadingSkeleton
						)}
					</ErrorBoundary>
				}
			>
				{autoCorrectPortal}
				<UFOSegment name="search-page-sain">
					<SmartAnswers
						resultsState={resultsState}
						isRelayEnabled
						smartAnswersPortalRef={smartAnswersPortalRef}
						fullWidthSainPortalRef={fullWidthSainPortalRef}
					/>
				</UFOSegment>
				{primaryProduct === PrimaryProductKeys.Assets && !fg('assets_rovo_search_all') && (
					<AssetsAppMessageBanner />
				)}
				<ErrorBoundary
					section="SearchPageRelayResults"
					setErrorBoundaryResetCb={setErrorBoundaryResetCb}
					onException={() => {
						searchResultsLoadUFOExperienceError();
						setResultsState(ResultsStates.ERROR);
					}}
					// Note: Using renderWithErrorInfo instead of fallback to ensure Error component
					// always receives actual error details (not generic "Error" message).
					// This enables:
					// 1. Specific error messages in VitalStats taskFail events (e.g., "NetworkError: Failed to fetch")
					// 2. Better error tracking and debugging
					// 3. Single rendering path for consistency
					renderWithErrorInfo={({ error, errorInfo }) => {
						// Evaluate feature gate unconditionally to ensure exposure tracking
						// (required by @atlaskit/platform/no-preconditioning linter rule)
						const showErrorDetails = fg('result_error_info_in_non_prod');
						return (
							<>
								{/* ErrorComponent shows detailed error stack trace in non-prod when flag is on */}
								{showErrorDetails && process.env.NODE_ENV !== 'production' && (
									<ErrorComponent error={error} errorInfo={errorInfo || {}} />
								)}
								{/* Error component shown to user, receives actual error for VitalStats taskFail event */}
								<Error fetchResults={() => window.location.reload()} error={error} />
							</>
						);
					}}
				>
					{mode === 'BOOTSTRAP_LOADING' ? (
						resultsConfigLoading
					) : mode === 'LANDING' ? (
						<UFOLoadHold name="search-page-results-landing">
							<Landing
								applicationMode={appMode}
								isAdminHubAIEnabled={isAdminHubAIEnabled}
								isRovoLLMEnabled={isRovoLLMEnabled}
							/>
						</UFOLoadHold>
					) : (
						<Suspense fallback={resultsLoading}>
							{mode === 'RESULTS' && relaySearchResults ? (
								<UFOSegment name="search-page-results">
									{newProductMessageBanner}
									<SearchResults
										searchResultsQuery={relaySearchResults}
										aggAbsoluteUrl={aggAbsoluteUrl}
										autoCorrectPortalRef={autoCorrectPortalRef}
										smartAnswersPortalRef={smartAnswersPortalRef}
										debugPortalRef={debugPortalRef}
										setResultsState={setResultsState}
										searchQuery={searchQuery}
										cloudId={cloudId}
										primaryProduct={primaryProduct}
										selectedProduct={selectedProduct}
										thirdPartyConfigs={thirdPartyConfigs}
										availableProducts={availableProducts}
										searchSessionId={searchSessionId}
										experimentConfig={experimentConfig}
										productEdition={productEdition}
										productDataRegions={productDataRegions}
										isJiraIssueIndexed={isJiraIssueIndexed}
										appMode={appMode}
										getSelectedFilters={getSelectedFilters}
										clearFilters={() => {
											clearAllFilters();
											resetSort();
										}}
										clearReferrer={clearReferrer}
										referrer={referrer}
										logError={logError}
										setFederatedSlackCount={setFederatedSlackCount}
										setFederatedOutlookMailCount={setFederatedOutlookMailCount}
										setFederatedGmailCount={setFederatedGmailCount}
										setProductsWithTimeout={setProductsWithTimeout}
										bodyRef={bodyRef}
										maxResultsToShow={maxResultsToShow}
										setMaxResultsToShow={setMaxResultsToShow}
										mainContentWidth={mainContentWidth}
										backendExperimentRef={backendExperimentRef}
									/>
								</UFOSegment>
							) : mode === 'CQL_RESULTS' && searchResultsCQLQueryReference ? (
								<UFOSegment name="search-page-results-cql">
									{newProductMessageBanner}
									<SearchResultsCQL
										availableProducts={availableProducts}
										backendExperimentRef={backendExperimentRef}
										bodyRef={bodyRef}
										clearReferrer={clearReferrer}
										cloudId={cloudId}
										experimentConfig={experimentConfig}
										getSelectedFilters={getSelectedFilters}
										isJiraIssueIndexed={isJiraIssueIndexed}
										maxResultsToShow={maxResultsToShow}
										productDataRegions={productDataRegions}
										productEdition={productEdition}
										referrer={referrer}
										searchQuery={searchQuery}
										searchResultsCQLQueryReference={searchResultsCQLQueryReference}
										searchSessionId={searchSessionId}
										selectedProduct={selectedProduct}
										primaryProduct={primaryProduct}
										appMode={appMode}
										mainContentWidth={mainContentWidth}
										setMaxResultsToShow={setMaxResultsToShow}
										setResultsState={setResultsState}
										thirdPartyConfigs={thirdPartyConfigs}
									/>
								</UFOSegment>
							) : (
								resultsLoading
							)}
						</Suspense>
					)}
				</ErrorBoundary>
			</Body>
		</>
	);

	return isSearchPagePOBannerPlacementEnabled ? (
		<PlacementPortalContainerProvider value={bannerPortalContainerRef.current}>
			<TrackImpressionProvider value={onBannerVisible}>{content}</TrackImpressionProvider>
		</PlacementPortalContainerProvider>
	) : (
		content
	);
});

export const SearchPageLayout = ({
	aggAbsoluteUrl,
	headerOffset,
}: SearchPageRelayProps): React.JSX.Element => {
	const { isNav4Enabled } = useAppContext();
	const [features] = useFeatures();
	const [searchQueryParams] = useQueryParams();
	const { primaryProduct } = useBootstrapState(getBootstrapVariables);

	const isSideBySideEnabled = features?.sideBySide?.enabled ?? false;
	const hasOverrides = Boolean(searchQueryParams?.overrides);
	const showDebugPanel =
		(isSideBySideEnabled || hasOverrides) && fg('enable_rovo_search_overrides');

	// Ref for the debug overrides panel portal target
	const debugPortalRef = useRef<HTMLDivElement>(null);
	const debugPortal = showDebugPanel ? <Box ref={debugPortalRef} /> : <></>;

	return (
		<Layout headerOffset={headerOffset}>
			{debugPortal}
			<Header
				primaryProduct={primaryProduct}
				input={isNav4Enabled ? null : <Input />}
				filters={<Filters />}
			/>
			<SearchPageBody aggAbsoluteUrl={aggAbsoluteUrl} debugPortalRef={debugPortalRef} />
			{serve3PBulkConnectWithoutExposure() && (
				<ErrorBoundary section="SearchPageBulkConnectModal">
					<BulkConnectModal
						isOnSearchDialog={false}
						oAuthSource={OAuthSource.searchPageBulkConnect}
					/>
				</ErrorBoundary>
			)}
		</Layout>
	);
};
